<template>
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="layout-header">
      <div class="header-left">
        <div class="patient-info">
          <IconCircle :initials="patientIcon" :size="32" bg-color="var(--icon-patient-bg)" color="var(--icon-patient-color)" />
          <h2 class="patient-name">{{ patientDisplayName }}</h2>
        </div>

        <TabBar :tabs="availableTabs" :active-tab="activeTab" @update:active-tab="handleTabChange" />
      </div>

      <div class="header-right">
        <a
          v-if="patient && patient.ehrId"
          :href="practiceFusionUrl"
          target="_blank"
          class="pf-link"
          title="Open in PracticeFusion"
        >
          <img src="/pf-logo.png" alt="PracticeFusion" class="pf-logo" />
        </a>

        <template v-if="mode === 'view' && activeTab === 'details'">
          <button class="button primary" @click="enterEditMode">Edit Patient</button>
        </template>

        <template v-else-if="mode === 'view' && activeTab === 'tickets'">
          <button class="button primary" @click="handleCreateTicket">Create Ticket</button>
        </template>

        <template v-else-if="mode === 'view' && activeTab === 'notes'">
          <button class="button primary" @click="openModal('create-note', {})">Create Note</button>
        </template>

        <template v-else-if="mode === 'edit' || mode === 'create'">
          <button class="button" @click="cancelEdit">Cancel</button>
          <button class="button primary" @click="saveChanges">
            {{ mode === 'create' ? 'Create Patient' : 'Save Changes' }}
          </button>
        </template>
      </div>
    </div>

    <div class="layout-body">
      <div v-if="activeTab === 'details'" class="card-container">
        <!-- Analytics Cards -->
        <div v-if="mode === 'view' && !isLoadingAnalytics" class="analytics-row">
          <CardLineChart
            class="analytics-card"
            title="Spend Over Time"
            :subtitle="'Total: ' + totalSpendFormatted"
            :data="spendOverTimeChartData"
            :options="spendOverTimeChartOptions"
            :formatTooltipValue="formatCurrencyValue"
            :height="200"
            variant="cumulative"
          />
          <CardBarChart
            class="analytics-card"
            title="Spend by Product"
            subtitle="Top 5 products"
            :data="spendByProductChartData"
            :options="spendByProductChartOptions"
            :horizontal="true"
            :height="200"
            :showLegend="false"
          />
          <CardBarChart
            class="analytics-card"
            title="Spend by Counselor"
            subtitle="Top 5 counselors"
            :data="spendByCounselorChartData"
            :options="spendByCounselorChartOptions"
            :horizontal="true"
            :height="200"
            :showLegend="false"
          />
        </div>

        <CardInputForm title="Personal Info" v-model="draft" :fields="personalInfoFields">
          <template #field-firstName>
            <TextInput v-model="draft.firstName" placeholder="Enter first name" />
          </template>
          <template #display-firstName="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-lastName>
            <TextInput v-model="draft.lastName" placeholder="Enter last name" />
          </template>
          <template #display-lastName="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-dateOfBirth>
            <DateInput v-model="draft.dateOfBirth" />
          </template>
          <template #display-dateOfBirth="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatUtils.formatDate(value) || 'No data' }}</span>
          </template>

          <template #field-age>
            <TextInput :disabled="true" :model-value="patientAge !== null ? `${patientAge} years` : 'No data'" />
          </template>
          <template #display-age>
            <span :class="{ 'no-data': patientAge === null }">
              {{ patientAge !== null ? `${patientAge} years` : 'No data' }}
            </span>
          </template>
        </CardInputForm>

        <CardInputForm title="Contact Info" v-model="draft" :fields="contactInfoFields">
          <template #field-email>
            <TextInput v-model="draft.email" placeholder="email@example.com" />
          </template>
          <template #display-email="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-mobilePhone>
            <PhoneNumberInput v-model="draft.mobilePhone" placeholder="(555) 555-5555" />
          </template>
          <template #display-mobilePhone="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatUtils.formatPhoneNumber(value) || 'No data' }}</span>
          </template>

          <template #field-homePhone>
            <PhoneNumberInput v-model="draft.homePhone" placeholder="(555) 555-5555" />
          </template>
          <template #display-homePhone="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatUtils.formatPhoneNumber(value) || 'No data' }}</span>
          </template>

          <template #field-workPhone>
            <PhoneNumberInput v-model="draft.workPhone" placeholder="(555) 555-5555" />
          </template>
          <template #display-workPhone="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatUtils.formatPhoneNumber(value) || 'No data' }}</span>
          </template>
        </CardInputForm>

        <CardInputForm title="Home Address" v-model="draft" :fields="addressFields">
          <template #field-addressStreet>
            <TextInput v-model="draft.addressStreet" placeholder="123 Main Street" />
          </template>
          <template #display-addressStreet="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-addressCity>
            <TextInput v-model="draft.addressCity" placeholder="Enter city" />
          </template>
          <template #display-addressCity="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-addressState>
            <TextInput v-model="draft.addressState" placeholder="Enter state" />
          </template>
          <template #display-addressState="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-addressZip>
            <TextInput v-model="draft.addressZip" placeholder="Enter ZIP code" />
          </template>
          <template #display-addressZip="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>
        </CardInputForm>

        <CardInputForm title="Other" v-model="draft" :fields="otherFields">
          <template #field-marketingSource>
            <SelectInput v-model="draft.marketingSource" :options="marketingSourceOptions" placeholder="Select marketing source" />
          </template>
          <template #display-marketingSource="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-ehrId>
            <TextInput v-model="draft.ehrId" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            <span v-if="ehrIdError" class="field-error">{{ ehrIdError }}</span>
          </template>
          <template #display-ehrId="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-notes>
            <TextareaInput v-model="draft.notes" placeholder="Enter notes..." :min-rows="3" />
          </template>
          <template #display-notes="{ value }">
            <span :class="{ 'no-data': !value }" class="notes-display">{{ value || 'No data' }}</span>
          </template>
        </CardInputForm>
      </div>
      <div v-if="activeTab === 'tickets'" class="tickets-container">
        <CustomTable
          :columns="visibleTicketColumnsFiltered"
          :rows="tickets"
          :sticky-first-column="true"
          :loading="isLoadingTickets"
          :is-empty="tickets.length === 0"
          :sort-by="ticketSortBy"
          :sort-order="ticketSortOrder"
          @row-click="handleTicketRowClick"
          @sort="handleTicketSort"
        >
          <template #column-ticketNumber="{ row }">
            <div class="ticket-cell">
              <IconCircle :size="28" bgColor="var(--icon-ticket-bg)" color="var(--icon-ticket-color)" :icon="getTicketIcon()" />
              <span class="cell-value name-text">Ticket #{{ row.ticketId }}</span>
            </div>
          </template>

          <template #column-date="{ row }">
            <span class="cell-value">{{ formatUtils.formatDate(row.dateTicketStart) }}</span>
          </template>

          <template #column-ticketType="{ row }">
            <span class="cell-value">{{ getTicketType(row) }}</span>
          </template>

          <template #column-counselor="{ row }">
            <span class="cell-value">{{ row.counselorName }}</span>
          </template>

          <template #column-status="{ row }">
            <StatusBadge :status="calculateTicketStatus(row)" />
          </template>

          <template #column-products="{ row }">
            <span class="cell-value products-cell">{{ formatProducts(row.products) }}</span>
          </template>

          <template #column-paymentDuration="{ row }">
            <span class="cell-value">{{ formatPaymentDuration(row) }}</span>
          </template>

          <template #column-total="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsTotal) }}</span>
          </template>

          <template #column-collected="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCollected) }}</span>
          </template>

          <template #column-cancelled="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCancelled) }}</span>
          </template>

          <template #column-balance="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsBalance) }}</span>
          </template>

          <template #column-dateNextPayment="{ row }">
            <span v-if="row.dateNextReceivable" class="cell-value">{{ formatUtils.formatDate(row.dateNextReceivable) }}</span>
            <span v-else class="no-data">No balance</span>
          </template>

        </CustomTable>
      </div>

      <div v-if="activeTab === 'payments'" class="payments-container">
        <CustomTable
          :columns="paymentColumns"
          :rows="payments"
          :sticky-first-column="true"
          :loading="isLoadingPayments"
          :is-empty="payments.length === 0"
          :sort-by="paymentSortBy"
          :sort-order="paymentSortOrder"
          @row-click="handlePaymentRowClick"
          @sort="handlePaymentSort"
        >
          <template #column-payment="{ row }">
            <span class="cell-value name-text">Payment #{{ row.paymentId }}</span>
          </template>

          <template #column-datePaid="{ row }">
            <span class="cell-value">{{ formatUtils.formatDate(row.datePaid) }}</span>
          </template>

          <template #column-ticket="{ row }">
            <span class="cell-value">Ticket #{{ row.ticketId }}</span>
          </template>

          <template #column-paymentType="{ row }">
            <span class="cell-value">{{ row.paymentType }}</span>
          </template>

          <template #column-paymentMethod="{ row }">
            <span class="cell-value">{{ formatPaymentMethod(row.paymentMethod) }}</span>
          </template>

          <template #column-amountPaid="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsPaid) }}</span>
          </template>

          <template #column-amountRefunded="{ row }">
            <span v-if="row.amountCentsRefunded > 0" class="cell-value refund-cell">
              {{ formatUtils.formatCurrency(row.amountCentsRefunded) }}
              <Tooltip v-if="row.dateRefunded" :text="getRefundTooltip(row)" position="top">
                <InformationOutlineIcon :size="14" class="refund-info-icon" />
              </Tooltip>
            </span>
            <span v-else class="no-data">No refund</span>
          </template>
        </CustomTable>
      </div>

      <div v-if="activeTab === 'notes'" class="notes-container">
        <div v-if="!isLoadingNotes && patientNotes.length === 0" class="notes-empty-state">
          <svg class="notes-empty-icon" viewBox="0 0 184 152" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" fill-rule="evenodd">
              <g transform="translate(24 31.67)">
                <ellipse fill-opacity=".8" fill="#F5F5F7" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
                <path d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z" fill="#AEB8C2" />
                <path d="M101.537 86.214L80.63 61.102c-1.001-1.207-2.507-1.867-4.048-1.867H31.724c-1.54 0-3.047.66-4.048 1.867L6.769 86.214v13.792h94.768V86.214z" fill="url(#notesGradient)" transform="translate(13.56)" />
                <path d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill="#F5F5F7" />
                <path d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393 0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z" fill="#DCE0E6" />
              </g>
              <path d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z" fill="#DCE0E6" />
              <g transform="translate(149.65 15.383)" fill="#FFF">
                <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
                <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
              </g>
              <defs>
                <linearGradient id="notesGradient" x1="50%" y1="0%" x2="50%" y2="100%">
                  <stop stop-color="#F5F5F7" offset="0%" />
                  <stop stop-color="#E1E3E6" offset="100%" />
                </linearGradient>
              </defs>
            </g>
          </svg>
          <h3 class="notes-empty-title">No notes found</h3>
          <p class="notes-empty-description">There are no notes to display at this time</p>
        </div>
        <div v-else-if="!isLoadingNotes" class="notes-list">
          <div
            v-for="note in patientNotes"
            :key="note.patientNoteId"
            class="note-card"
            :class="{ 'note-inactive': !note.isActive }"
            @click="openModal('edit-note', { note })"
          >
            <div class="note-card-header">
              <span class="note-type-badge" :class="note.type">
                {{ note.type === 'product_log' ? 'Product Log' : 'Misc' }}
              </span>
              <span v-if="note.productName" class="note-product">{{ note.productName }}</span>
              <span v-if="!note.isActive" class="note-inactive-badge">Inactive</span>
              <span class="note-date">{{ formatUtils.formatDate(note.dateCreated) }}</span>
            </div>
            <div class="note-card-body">
              <p class="note-description">{{ note.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'tickets'" class="layout-footer">
      <PaginationControl
        :current-page="currentPage"
        :total-items="totalTickets"
        :items-per-page="itemsPerPage"
        @page-change="handlePageChange"
      />
    </div>

    <div v-if="activeTab === 'payments'" class="layout-footer">
      <PaginationControl
        :current-page="paymentsCurrentPage"
        :total-items="totalPayments"
        :items-per-page="paymentsItemsPerPage"
        @page-change="handlePaymentsPageChange"
      />
    </div>

    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <PatientNoteModal
              v-if="modal.id === 'create-note'"
              :patientId="parseInt(patientId)"
              @note-saved="handleNoteSaved"
            />
            <PatientNoteModal
              v-else-if="modal.id === 'edit-note'"
              :patientId="parseInt(patientId)"
              :note="modal.data.note"
              @note-saved="handleNoteSaved"
            />
          </transition>
        </div>
      </div>
    </transition>

  </AppLayout>
</template>

<script>
import AppLayout from '@/components/app/AppLayout.vue';
import TabBar from '@/components/common/TabBar.vue';
import CardInputForm from '@/components/common/CardInputForm.vue';
import CardLineChart from '@/components/common/CardLineChart.vue';
import CardBarChart from '@/components/common/CardBarChart.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import StatusBadge from '@/components/common/StatusBadge.vue';
import CustomTable from '@/components/common/CustomTable.vue';
import PaginationControl from '@/components/common/PaginationControl.vue';
import PhoneNumberInput from '@/components/inputs/PhoneNumberInput.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import TextareaInput from '@/components/inputs/TextareaInput.vue';
import DateInput from '@/components/inputs/DateInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import PatientNoteModal from '@/modals/PatientNoteModal.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import InformationOutlineIcon from 'vue-material-design-icons/InformationOutline.vue';
import TicketIcon from 'vue-material-design-icons/Ticket.vue';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { calculateTicketStatus } from '@/utils/formatters';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';

export default {
  name: 'PatientRecordView',
  computed: {
    patientId() {
      return this.$route.params.id;
    },
    patientLink() {
      return '/patients/' + this.patientId;
    },
    breadcrumbs() {
      return [
        { label: 'Patients', link: '/patients' },
        { label: this.patientDisplayName || 'Patient', link: this.patientLink }
      ];
    },
    patientDisplayName() {
      if (this.mode === 'create') return 'New Patient';
      if (!this.patient) return 'Loading...';
      const firstName = this.patient.firstName || '';
      const lastName = this.patient.lastName || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown Patient';
    },
    patientIcon() {
      if (this.mode === 'create') return 'NP';
      if (!this.patient) return 'U';
      const firstName = this.patient.firstName || '';
      const lastName = this.patient.lastName || '';
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U';
    },
    practiceFusionUrl() {
      if (!this.patient || !this.patient.ehrId) return '';
      return `https://static.practicefusion.com/apps/ehr/index.html#/PF/charts/patients/${this.patient.ehrId}/summary`;
    },
    ehrIdError() {
      const val = this.draft.ehrId;
      if (!val) return '';
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!guidRegex.test(val)) return 'Must be a valid GUID (e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)';
      return '';
    },
    isReadonly() {
      return this.mode === 'view';
    },
    visibleTicketColumnsFiltered() {
      return this.ticketColumns.filter(col => {
        // Always show first column (ticketNumber)
        if (col.key === 'ticketNumber') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.ticketColumnVisibility[col.key] !== false;
      });
    },
    patientAge() {
      if (!this.draft.dateOfBirth) return null;

      const today = new Date();
      const birthDate = new Date(this.draft.dateOfBirth);

      if (isNaN(birthDate.getTime())) return null;

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      // Adjust age if birthday hasn't occurred yet this year
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    },
    personalInfoFields() {
      return [
        {
          name: 'firstName',
          label: 'First Name',
          readonly: this.isReadonly,
          required: true
        },
        {
          name: 'lastName',
          label: 'Last Name',
          readonly: this.isReadonly,
          required: true
        },
        {
          name: 'dateOfBirth',
          label: 'Date of Birth',
          readonly: this.isReadonly,
          required: true
        },
        {
          name: 'age',
          label: 'Age',
          readonly: this.isReadonly
        }
      ];
    },
    contactInfoFields() {
      return [
        {
          name: 'email',
          label: 'Email Address',
          readonly: this.isReadonly
        },
        {
          name: 'mobilePhone',
          label: 'Mobile Phone',
          readonly: this.isReadonly
        },
        {
          name: 'homePhone',
          label: 'Home Phone',
          readonly: this.isReadonly
        },
        {
          name: 'workPhone',
          label: 'Work Phone',
          readonly: this.isReadonly
        }
      ];
    },
    addressFields() {
      return [
        {
          name: 'addressStreet',
          label: 'Street Address',
          readonly: this.isReadonly
        },
        {
          name: 'addressCity',
          label: 'City',
          readonly: this.isReadonly
        },
        {
          name: 'addressState',
          label: 'State',
          readonly: this.isReadonly
        },
        {
          name: 'addressZip',
          label: 'ZIP Code',
          readonly: this.isReadonly
        }
      ];
    },
    otherFields() {
      return [
        {
          name: 'marketingSource',
          label: 'Marketing Source',
          readonly: this.isReadonly
        },
        {
          name: 'ehrId',
          label: 'EHR ID',
          readonly: this.isReadonly
        },
        {
          name: 'notes',
          label: 'Notes',
          readonly: this.isReadonly
        }
      ];
    },
    availableTabs() {
      const isEditMode = this.mode === 'create' || this.mode === 'edit';
      return [
        { key: 'details', label: 'Details', disabled: false },
        { key: 'tickets', label: 'Tickets', disabled: isEditMode },
        { key: 'payments', label: 'Payments', disabled: isEditMode },
        { key: 'notes', label: 'Notes', disabled: isEditMode }
      ];
    },
    activeTab() {
      const tab = this.$route.params.tab;
      const validTabs = this.availableTabs.map(t => t.key);
      if (tab && validTabs.includes(tab)) {
        return tab;
      }
      return 'details';
    },

    // Analytics chart data
    spendOverTimeChartData() {
      if (!this.analytics?.monthlySpend || this.analytics.monthlySpend.length === 0) return null;

      return {
        labels: this.analytics.monthlySpend.map(m => m.month),
        datasets: [
          {
            label: 'Spend',
            data: this.analytics.monthlySpend.map(m => m.amountCents / 100),
            color: '#4338ca'
          }
        ]
      };
    },

    spendOverTimeChartOptions() {
      return {
        yAxis: {
          axisLabel: {
            formatter: (value) => this.formatCurrencyShort(value)
          }
        }
      };
    },

    totalSpendFormatted() {
      if (!this.analytics?.totalSpend) return '$0.00';
      return this.formatCurrencyValue(this.analytics.totalSpend.amountCentsTotal / 100);
    },

    spendByProductChartData() {
      if (!this.analytics?.topProducts || this.analytics.topProducts.length === 0) return null;

      const topProducts = this.analytics.topProducts.slice(0, 5);
      const otherProducts = this.analytics.topProducts.slice(5);

      const chartData = [...topProducts];
      if (otherProducts.length > 0) {
        const otherTotal = otherProducts.reduce((sum, p) => sum + p.amountCentsTotal, 0);
        if (otherTotal > 0) {
          chartData.push({
            productId: -1,
            productName: 'Other',
            amountCentsTotal: otherTotal
          });
        }
      }

      // Reverse so highest is at top in horizontal bar
      const reversed = chartData.reverse();

      return {
        labels: reversed.map(p => p.productName),
        datasets: [
          {
            label: 'Spend',
            data: reversed.map(p => p.amountCentsTotal / 100),
            color: '#4338ca'
          }
        ]
      };
    },

    spendByProductChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];
            const param = params[0];
            const value = param.value || 0;
            return `<div style="font-weight: 600; margin-bottom: 8px;">${param.axisValue}</div>
              <div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
                <span style="flex: 1;">Spend:</span>
                <span style="font-weight: 600; margin-left: 12px;">${this.formatCurrencyValue(value)}</span>
              </div>`;
          }
        }
      };
    },

    spendByCounselorChartData() {
      if (!this.analytics?.topCounselors || this.analytics.topCounselors.length === 0) return null;

      const topCounselors = this.analytics.topCounselors.slice(0, 5);
      const otherCounselors = this.analytics.topCounselors.slice(5);

      const chartData = [...topCounselors];
      if (otherCounselors.length > 0) {
        const otherTotal = otherCounselors.reduce((sum, c) => sum + c.amountCentsTotal, 0);
        if (otherTotal > 0) {
          chartData.push({
            counselorId: -1,
            counselorName: 'Other',
            amountCentsTotal: otherTotal
          });
        }
      }

      // Reverse so highest is at top in horizontal bar
      const reversed = chartData.reverse();

      return {
        labels: reversed.map(c => c.counselorName),
        datasets: [
          {
            label: 'Spend',
            data: reversed.map(c => c.amountCentsTotal / 100),
            color: '#4338ca'
          }
        ]
      };
    },

    spendByCounselorChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];
            const param = params[0];
            const value = param.value || 0;
            return `<div style="font-weight: 600; margin-bottom: 8px;">${param.axisValue}</div>
              <div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
                <span style="flex: 1;">Spend:</span>
                <span style="font-weight: 600; margin-left: 12px;">${this.formatCurrencyValue(value)}</span>
              </div>`;
          }
        }
      };
    }
  },
  data() {
    return {
      patient: null,
      isLoading: false,
      mode: 'view',
      draft: {},
      analytics: null,
      isLoadingAnalytics: false,
      formatUtils,
      marketingSourceOptions: [
        { value: '101.1 ESPN', label: '101.1 ESPN' },
        { value: '1120 KMOX', label: '1120 KMOX' },
        { value: '90.7 NPR', label: '90.7 NPR' },
        { value: '94.7 KSHE', label: '94.7 KSHE' },
        { value: '97.1 KFTK', label: '97.1 KFTK' },
        { value: 'Call Center', label: 'Call Center' },
        { value: 'Ch. 2 KTVI (Fox)', label: 'Ch. 2 KTVI (Fox)' },
        { value: 'Ch. 4 KMOV (CBS)', label: 'Ch. 4 KMOV (CBS)' },
        { value: 'Ch. 5 KSDK (NBC)', label: 'Ch. 5 KSDK (NBC)' },
        { value: 'Current Patient', label: 'Current Patient' },
        { value: 'Direct Mailer', label: 'Direct Mailer' },
        { value: 'KPLR- Channel 11', label: 'KPLR- Channel 11' },
        { value: 'Online/Website', label: 'Online/Website' },
        { value: 'Post Dispatch', label: 'Post Dispatch' },
        { value: 'Referral', label: 'Referral' },
        { value: 'SHIPPING', label: 'SHIPPING' },
        { value: 'Uncertain Radio', label: 'Uncertain Radio' },
        { value: 'Uncertain TV', label: 'Uncertain TV' },
        { value: 'Other', label: 'Other' }
      ],
      // Notes-related state
      patientNotes: [],
      isLoadingNotes: false,
      modal: {
        open: false,
        id: '',
        data: {}
      },
      // Ticket-related state
      tickets: [],
      totalTickets: 0,
      currentPage: 1,
      itemsPerPage: 100,
      isLoadingTickets: false,
      ticketSortBy: 'dateTicketStart',
      ticketSortOrder: 'desc',
      ticketColumnVisibility: {},
      ticketColumns: [
        { key: 'ticketNumber', label: 'Ticket ID', width: '200px' },
        { key: 'date', label: 'Ticket Date', width: '200px', sortable: true, sortKey: 'dateTicketStart' },
        { key: 'ticketType', label: 'Ticket Type', width: '200px' },
        { key: 'counselor', label: 'Counselor', width: '200px' },
        { key: 'status', label: 'Status', width: '200px' },
        { key: 'products', label: 'Products', width: '200px' },
        { key: 'paymentDuration', label: 'Payment Duration', width: '200px' },
        { key: 'total', label: 'Total Amount', width: '200px', sortable: true, sortKey: 'amountCentsTotal' },
        { key: 'collected', label: 'Amount Collected', width: '200px', sortable: true, sortKey: 'amountCentsCollected' },
        { key: 'cancelled', label: 'Amount Cancelled', width: '200px', sortable: true, sortKey: 'amountCentsCancelled' },
        { key: 'balance', label: 'Current Balance', width: '200px', sortable: true, sortKey: 'amountCentsBalance' },
        { key: 'dateNextPayment', label: 'Date Next Payment', width: '200px', sortable: true, sortKey: 'dateNextReceivable' }
      ],
      // Payments-related state
      payments: [],
      totalPayments: 0,
      paymentsCurrentPage: 1,
      paymentsItemsPerPage: 20,
      isLoadingPayments: false,
      paymentSortBy: 'datePaid',
      paymentSortOrder: 'desc',
      paymentColumns: [
        { key: 'payment', label: 'Payment ID', width: '180px' },
        { key: 'datePaid', label: 'Date Paid', width: '150px', sortable: true, sortKey: 'datePaid' },
        { key: 'ticket', label: 'Ticket', width: '150px' },
        { key: 'paymentType', label: 'Payment Type', width: '180px' },
        { key: 'paymentMethod', label: 'Payment Method', width: '150px' },
        { key: 'amountPaid', label: 'Amount Paid', width: '180px', sortable: true, sortKey: 'amountCentsPaid' },
        { key: 'amountRefunded', label: 'Amount Refunded', width: '200px', sortable: true, sortKey: 'amountCentsRefunded' }
      ]
    };
  },
  watch: {
    'draft.ehrId'(val) {
      if (!val) return;
      const match = val.match(/\/charts\/patients\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      if (match) {
        this.draft.ehrId = match[1];
      }
    },
    currentPage() {
      this.loadTickets();
    },
    itemsPerPage() {
      this.loadTickets();
    },
    activeTab: {
      handler(newTab) {
        if (newTab === 'tickets') {
          this.loadTickets();
        }
        if (newTab === 'payments') {
          this.loadPayments();
        }
        if (newTab === 'notes') {
          this.loadNotes();
        }
      },
      immediate: true
    },
    ticketSortBy() {
      this.currentPage = 1;
      this.loadTickets();
    },
    ticketSortOrder() {
      this.currentPage = 1;
      this.loadTickets();
    },
    paymentsCurrentPage() {
      this.loadPayments();
    },
    paymentSortBy() {
      this.paymentsCurrentPage = 1;
      this.loadPayments();
    },
    paymentSortOrder() {
      this.paymentsCurrentPage = 1;
      this.loadPayments();
    }
  },
  created() {
    this.loadTicketColumnVisibility();
    if (this.patientId === 'new') {
      this.enterCreateMode();
    } else {
      this.load();
    }
  },
  methods: {
    getTicketIcon() {
      return TicketIcon;
    },
    async load() {
      try {
        this.isLoading = true;

        const patient = await queryClient.fetchQuery({
          queryKey: ['patient', this.patientId],
          queryFn: () => api.fetchPatientById(this.patientId)
        });

        this.patient = patient;
        this.draft = structuredClone(this.patient);

        // Load analytics for existing patients
        this.loadAnalytics();

      } catch (err) {
        console.error('Failed to fetch patient:', err);
        alertService.error('Failed to load patient: ' + err.message);
        this.$router.push('/patients');
      } finally {
        this.isLoading = false;
      }
    },
    async loadAnalytics() {
      if (this.mode === 'create') return;

      try {
        this.isLoadingAnalytics = true;
        const analytics = await queryClient.fetchQuery({
          queryKey: ['patient-analytics', this.patientId],
          queryFn: () => api.fetchPatientAnalytics(this.patientId)
        });
        this.analytics = analytics;
      } catch (err) {
        console.error('Failed to fetch patient analytics:', err);
      } finally {
        this.isLoadingAnalytics = false;
      }
    },
    formatCurrencyValue(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    },
    formatCurrencyShort(value) {
      if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return '$' + (value / 1000).toFixed(0) + 'K';
      }
      return '$' + value.toFixed(2);
    },
    handleTabChange(tabKey) {
      const basePath = `/patients/${this.patientId}`;
      if (tabKey === 'details') {
        this.$router.push(basePath);
      } else {
        this.$router.push(`${basePath}/${tabKey}`);
      }
    },
    enterEditMode() {
      if (this.activeTab !== 'details') {
        this.$router.push(`/patients/${this.patientId}`);
      }
      this.draft = structuredClone(this.patient);
      this.mode = 'edit';
    },
    enterCreateMode() {
      this.draft = this.getEmptyDraft();
      this.mode = 'create';
    },
    cancelEdit() {
      if (this.mode === 'create') {
        this.$router.push('/patients');
      } else {
        this.draft = structuredClone(this.patient);
        this.mode = 'view';
      }
    },
    getEmptyDraft() {
      return {
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        marketingSource: '',
        email: '',
        mobilePhone: '',
        homePhone: '',
        workPhone: '',
        addressStreet: '',
        addressCity: '',
        addressState: '',
        addressZip: '',
        notes: '',
        ehrId: ''
      };
    },
    transformDraftToPayload(draft) {
      return {
        firstName: draft.firstName,
        lastName: draft.lastName,
        dateOfBirth: draft.dateOfBirth || null,
        marketingSource: draft.marketingSource || null,
        email: draft.email || null,
        mobilePhone: draft.mobilePhone || null,
        homePhone: draft.homePhone || null,
        workPhone: draft.workPhone || null,
        addressStreet: draft.addressStreet || null,
        addressCity: draft.addressCity || null,
        addressState: draft.addressState || null,
        addressZip: draft.addressZip || null,
        notes: draft.notes || null,
        ehrId: draft.ehrId || null
      };
    },
    async saveChanges() {
      if (this.ehrIdError) {
        alertService.error('EHR ID: ' + this.ehrIdError);
        return;
      }
      try {
        const payload = this.transformDraftToPayload(this.draft);
        let updatedPatient;

        if (this.mode === 'edit') {
          updatedPatient = await api.updatePatient(this.patientId, payload);
          alertService.success('Patient updated successfully');
        }

        if (this.mode === 'create') {
          updatedPatient = await api.createPatient(payload);
          alertService.success('Patient created successfully');
          this.$router.replace(`/patients/${updatedPatient.patientId}`);
        }

        // Invalidate caches
        queryClient.invalidateQueries({ queryKey: ['patients'] });
        queryClient.invalidateQueries({ queryKey: ['patient', this.patientId] });

        // Update patient
        this.patient = updatedPatient;
        this.draft = structuredClone(this.patient);
        this.mode = 'view';

      } catch (err) {
        console.error('Failed to save patient:', err);
        alertService.error('Failed to save patient: ' + err.message);
      }
    },
    async loadTickets() {
      try {
        this.isLoadingTickets = true;

        const offset = (this.currentPage - 1) * this.itemsPerPage;
        const response = await queryClient.fetchQuery({
          queryKey: ['tickets', { patientId: this.patientId, limit: this.itemsPerPage, offset, sortBy: this.ticketSortBy, sortOrder: this.ticketSortOrder }],
          queryFn: () => api.fetchTickets({
            patientId: this.patientId,
            limit: this.itemsPerPage,
            offset,
            sortBy: this.ticketSortBy,
            sortOrder: this.ticketSortOrder
          })
        });

        this.tickets = response.tickets;
        this.totalTickets = response.total;

      } catch (err) {
        console.error('Failed to fetch tickets:', err);
        alertService.error('Failed to load tickets: ' + err.message);
        this.tickets = [];
        this.totalTickets = 0;
      } finally {
        this.isLoadingTickets = false;
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleTicketRowClick({ rowIndex }) {
      const ticket = this.tickets[rowIndex];
      if (ticket && ticket.ticketId) {
        this.$router.push(`/tickets/${ticket.ticketId}`);
      }
    },
    handleTicketSort({ sortBy, sortOrder }) {
      this.ticketSortBy = sortBy;
      this.ticketSortOrder = sortOrder;
    },
    getCounselorInitials(ticket) {
      const name = ticket.counselorName?.charAt(0) || '';
      return name.toUpperCase();
    },
    handleCreateTicket() {
      this.$router.push(`/tickets/new?patientId=${this.patientId}`);
    },
    async loadPayments() {
      try {
        this.isLoadingPayments = true;
        const offset = (this.paymentsCurrentPage - 1) * this.paymentsItemsPerPage;
        const response = await queryClient.fetchQuery({
          queryKey: ['payments', { patientId: this.patientId, limit: this.paymentsItemsPerPage, offset, sortBy: this.paymentSortBy, sortOrder: this.paymentSortOrder }],
          queryFn: () => api.fetchPayments({
            patientId: this.patientId,
            limit: this.paymentsItemsPerPage,
            offset,
            sortBy: this.paymentSortBy,
            sortOrder: this.paymentSortOrder
          })
        });
        this.payments = response.payments || [];
        this.totalPayments = response.total || 0;
      } catch (err) {
        console.error('Failed to fetch payments:', err);
        alertService.error('Failed to load payments: ' + err.message);
        this.payments = [];
        this.totalPayments = 0;
      } finally {
        this.isLoadingPayments = false;
      }
    },
    handlePaymentsPageChange(page) {
      this.paymentsCurrentPage = page;
    },
    handlePaymentSort({ sortBy, sortOrder }) {
      this.paymentSortBy = sortBy;
      this.paymentSortOrder = sortOrder;
    },
    handlePaymentRowClick({ rowIndex }) {
      const payment = this.payments[rowIndex];
      if (payment && payment.ticketId) {
        this.$router.push(`/tickets/${payment.ticketId}/payments`);
      }
    },
    formatPaymentMethod(method) {
      if (!method) return 'Unknown';
      const labels = {
        cash: 'Cash',
        check: 'Check',
        mastercard: 'Mastercard',
        visa: 'Visa',
        amex: 'AMX',
        discover: 'Discover',
        cashiers_check: 'Cashiers Check',
        agms: 'AGMS'
      };
      return labels[method] || method.charAt(0).toUpperCase() + method.slice(1);
    },
    getRefundTooltip(row) {
      let text = `Refunded on ${formatUtils.formatDate(row.dateRefunded)}`;
      if (row.reasonRefunded) {
        text += `\nReason: ${row.reasonRefunded}`;
      }
      return text;
    },
    async loadNotes() {
      try {
        this.isLoadingNotes = true;
        const response = await queryClient.fetchQuery({
          queryKey: ['patient-notes', { patientId: this.patientId }],
          queryFn: () => api.fetchPatientNotes({ patientId: this.patientId, limit: 200 })
        });
        this.patientNotes = response.patientNotes;
      } catch (err) {
        console.error('Failed to fetch patient notes:', err);
        alertService.error('Failed to load notes: ' + err.message);
        this.patientNotes = [];
      } finally {
        this.isLoadingNotes = false;
      }
    },
    openModal(id, data) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
        this.modal.data = data;
      });
    },
    closeModal() {
      this.modal.id = '';
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      if (event.target.classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    handleNoteSaved() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['patient-notes'] });
      this.loadNotes();
    },
    calculateTicketStatus(ticket) {
      return calculateTicketStatus(ticket);
    },
    formatPaymentDuration(ticket) {
      if (!ticket.dateFirstReceivable || !ticket.dateLastReceivable) {
        return '—';
      }
      const first = new Date(ticket.dateFirstReceivable);
      const last = new Date(ticket.dateLastReceivable);
      const months = (last.getFullYear() - first.getFullYear()) * 12 + (last.getMonth() - first.getMonth()) + 1;
      if (months <= 1) {
        return 'Pay in Full';
      }
      return `${months} Months`;
    },
    formatProducts(products) {
      if (!products || products.length === 0) {
        return '—';
      }
      return products.map(p => `${p.name} (x${p.quantity})`).join(', ');
    },
    getTicketType(ticket) {
      return ticket.isFirstTicketForPatient ? 'New Patient' : 'Current Patient';
    },
    loadTicketColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        const saved = localStorage.getItem(`shawncrm_columns_${practiceId}_tickets`);
        if (saved) {
          this.ticketColumnVisibility = JSON.parse(saved);
        } else {
          // Default columns: Ticket Date, Ticket Type, Patient, Counselor, Status, Products, Total Amount, Current Balance
          // Note: Patient column doesn't exist in PatientRecordView, so it's excluded
          const defaultVisibleColumns = ['date', 'ticketType', 'counselor', 'status', 'products', 'total', 'balance'];
          this.ticketColumns.forEach(col => {
            this.ticketColumnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
          });
        }
      } catch (err) {
        console.error('Failed to load ticket column visibility:', err);
        // Default columns on error
        const defaultVisibleColumns = ['date', 'ticketType', 'counselor', 'status', 'products', 'total', 'balance'];
        this.ticketColumns.forEach(col => {
          this.ticketColumnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
        });
      }
    }
  },
  components: { AppLayout, TabBar, CardInputForm, CardLineChart, CardBarChart, IconCircle, StatusBadge, CustomTable, PaginationControl, PhoneNumberInput, TextInput, TextareaInput, DateInput, SelectInput, PatientNoteModal, Tooltip, InformationOutlineIcon }
}
</script>

<style scoped>
.analytics-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
}

.analytics-card {
  border-right: 1px solid #e5e7eb;
}

.analytics-card:last-child {
  border-right: none;
}

@media (max-width: 1024px) {
  .analytics-row {
    grid-template-columns: 1fr;
  }

  .analytics-card {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .analytics-card:last-child {
    border-bottom: none;
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  min-width: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.patient-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.patient-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.pf-link {
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  opacity: 0.7;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.pf-link:hover {
  opacity: 1;
  transform: scale(1.1);
}

.pf-logo {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.card-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

.layout-header .button {
  white-space: nowrap;
}

/* Tickets tab container */
.tickets-container {
  height: 100%;
  width: 100%;
}

/* Payments tab container */
.payments-container {
  height: 100%;
  width: 100%;
}

.refund-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.refund-info-icon {
  color: #9ca3af;
  cursor: help;
}

.refund-info-icon:hover {
  color: #6b7280;
}

/* Cell styling */
.ticket-cell,
.counselor-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.products-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notes-display {
  white-space: pre-wrap;
  word-break: break-word;
}

.field-error {
  display: block;
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

/* Notes tab */
.notes-container {
  height: 100%;
  width: 100%;
}

.notes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 10px;
}

.note-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 16px 20px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.note-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.note-card.note-inactive {
  opacity: 0.6;
}

.note-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.note-type-badge {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
}

.note-type-badge.product_log {
  background: #ede9fe;
  color: #5b21b6;
}

.note-product {
  font-size: 13px;
  color: #4338ca;
  font-weight: 500;
}

.note-date {
  font-size: 12px;
  color: #9ca3af;
  margin-left: auto;
}

.note-inactive-badge {
  font-size: 11px;
  font-weight: 500;
  color: #d97706;
  background: #fef3c7;
  padding: 2px 6px;
  border-radius: 4px;
}

.note-description {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  white-space: pre-wrap;
  margin: 0;
}

.notes-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.notes-empty-icon {
  width: 180px;
  height: auto;
  margin-bottom: 24px;
  opacity: 0.8;
}

.notes-empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.notes-empty-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}
</style>
